// GENERATED by scripts/gen-icons.mjs — do not edit.
// Run `npm run gen:icons` after bumping lucide-react.

import type { LucideIcon } from "lucide-react";

/** One lazily fetched range of icons. */
export interface IconShard {
    /** The chunk's module id — what shows up in a bundle and in a 404. */
    id: string;
    /** First slug in the range, in sort order. Its lower bound. */
    from: string;
    /** How many icons the range holds. */
    size: number;
    /** The dynamic import for the chunk. */
    load: () => Promise<{ default: Record<string, LucideIcon> }>;
}

/**
 * Every shard, in slug order.
 *
 * These are the only references to the shard modules, which is what keeps them
 * out of whatever chunk imports `Icon`: a bundler that sees no static edge to a
 * shard emits it as an async chunk fetched on first use.
 *
 * Sorted by `from`, so the owning shard for a slug is a binary search over this
 * array — 46 comparisons' worth of index instead of the 2000-entry
 * slug→chunk map that makes lucide's own `dynamicIconImports` cost 120 KB in the
 * main chunk.
 */
export const iconShards: readonly IconShard[] = [
    { id: "shard-00", from: "a-arrow-down", size: 40, load: () => import("./shard-00") },
    { id: "shard-01", from: "amphora", size: 40, load: () => import("./shard-01") },
    { id: "shard-02", from: "arrow-right-left", size: 40, load: () => import("./shard-02") },
    { id: "shard-03", from: "badge-plus", size: 40, load: () => import("./shard-03") },
    { id: "shard-04", from: "bell-dot", size: 40, load: () => import("./shard-04") },
    { id: "shard-05", from: "book-heart", size: 40, load: () => import("./shard-05") },
    { id: "shard-06", from: "briefcase", size: 40, load: () => import("./shard-06") },
    { id: "shard-07", from: "calendar-search", size: 40, load: () => import("./shard-07") },
    { id: "shard-08", from: "chart-column-big", size: 40, load: () => import("./shard-08") },
    { id: "shard-09", from: "chevrons-up-down", size: 40, load: () => import("./shard-09") },
    { id: "shard-10", from: "circle-pound-sterling", size: 40, load: () => import("./shard-10") },
    { id: "shard-11", from: "clock-arrow-down", size: 40, load: () => import("./shard-11") },
    { id: "shard-12", from: "combine", size: 40, load: () => import("./shard-12") },
    { id: "shard-13", from: "cross", size: 40, load: () => import("./shard-13") },
    { id: "shard-14", from: "dna", size: 40, load: () => import("./shard-14") },
    { id: "shard-15", from: "euro", size: 40, load: () => import("./shard-15") },
    { id: "shard-16", from: "file-down", size: 40, load: () => import("./shard-16") },
    { id: "shard-17", from: "fish", size: 40, load: () => import("./shard-17") },
    { id: "shard-18", from: "folder-minus", size: 40, load: () => import("./shard-18") },
    { id: "shard-19", from: "ghost", size: 40, load: () => import("./shard-19") },
    { id: "shard-20", from: "guitar", size: 40, load: () => import("./shard-20") },
    { id: "shard-21", from: "heart-x", size: 40, load: () => import("./shard-21") },
    { id: "shard-22", from: "kayak", size: 40, load: () => import("./shard-22") },
    { id: "shard-23", from: "lens-convex", size: 40, load: () => import("./shard-23") },
    { id: "shard-24", from: "loader-pinwheel", size: 40, load: () => import("./shard-24") },
    { id: "shard-25", from: "map-pin-search", size: 40, load: () => import("./shard-25") },
    { id: "shard-26", from: "message-square-quote", size: 40, load: () => import("./shard-26") },
    { id: "shard-27", from: "moon-star", size: 40, load: () => import("./shard-27") },
    { id: "shard-28", from: "nfc", size: 40, load: () => import("./shard-28") },
    { id: "shard-29", from: "panel-left-open", size: 40, load: () => import("./shard-29") },
    { id: "shard-30", from: "phone-incoming", size: 40, load: () => import("./shard-30") },
    { id: "shard-31", from: "presentation", size: 40, load: () => import("./shard-31") },
    { id: "shard-32", from: "redo-dot", size: 40, load: () => import("./shard-32") },
    { id: "shard-33", from: "ruler", size: 40, load: () => import("./shard-33") },
    { id: "shard-34", from: "search-code", size: 40, load: () => import("./shard-34") },
    { id: "shard-35", from: "ship-wheel", size: 40, load: () => import("./shard-35") },
    { id: "shard-36", from: "soup", size: 40, load: () => import("./shard-36") },
    { id: "shard-37", from: "square-chevron-down", size: 40, load: () => import("./shard-37") },
    { id: "shard-38", from: "square-split-horizontal", size: 40, load: () => import("./shard-38") },
    { id: "shard-39", from: "stretch-vertical", size: 40, load: () => import("./shard-39") },
    { id: "shard-40", from: "telescope", size: 40, load: () => import("./shard-40") },
    { id: "shard-41", from: "toolbox", size: 40, load: () => import("./shard-41") },
    { id: "shard-42", from: "umbrella-off", size: 40, load: () => import("./shard-42") },
    { id: "shard-43", from: "utensils-crossed", size: 40, load: () => import("./shard-43") },
    { id: "shard-44", from: "waves-vertical", size: 40, load: () => import("./shard-44") },
    { id: "shard-45", from: "zodiac-pisces", size: 7, load: () => import("./shard-45") },
];
